/**
 * userpreferencesaction.js
 * Created by calhosh on 8/28/2020.
 */
import * as types from '../actiontypes';
import {GetByIdInternalUserUrl} from '../apiUrls';
import env from '../../util/env';

export function getUserPreferences(url, resource) {
	return {
		type: types.GET_USER_PREFERENCES,
		meta: {
			api: {
				url: url,
				method: 'GET',
				data: {resource: resource}
			}
		}
	};
}

/**
 * Fetches the authoritative user settings from Universal (the system of record).
 * The response is resolved back to the caller (resolve: true) and intentionally NOT stored in
 * Redux: it is only used to build the full PUT payload, so no reducer/manifest entry exists for it.
 * @returns {Object} An action object treated by the api-middleware (GET Universal users.json).
 */
export function getUserSettings() {
	return {
		type: types.GET_USER_SETTINGS,
		resolve: true,
		meta: {
			api: {
				url: env.getUniversalUrl('/users.json'),
				method: 'GET'
			}
		}
	};
}

/**
 * Re-maps the keys of newValues to the key casing used by reference (the authoritative Universal
 * settings), keeping ONLY the keys that strictly exist in reference (matched case-insensitively).
 * Any key in newValues without a counterpart in reference is omitted, with no exception, so the
 * PUT never sends fields Universal does not recognize. A matched value adopts reference's casing,
 * guaranteeing it overwrites the correct field instead of creating a duplicate.
 * @param {Object} reference The object whose keys are authoritative (userSettings.data).
 * @param {Object} newValues The caller-provided values, possibly with inconsistent key casing.
 * @returns {Object} The subset of newValues whose keys exist in reference, keyed by reference's casing.
 */
function normalizeKeysToReference(reference, newValues) {
	const referenceKeyByLower = {};
	Object.keys(reference || {}).forEach((key) => {
		referenceKeyByLower[key.toLowerCase()] = key;
	});

	const normalized = {};
	Object.keys(newValues || {}).forEach((key) => {
		const referenceKey = referenceKeyByLower[key.toLowerCase()];
		if (referenceKey) {
			normalized[referenceKey] = newValues[key];
		}
	});
	return normalized;
}

/**
 * Updates the user-settings slice with updatedValues. When an api descriptor is provided, the
 * api-middleware persists the change (the reducer then runs on success); when it is omitted the
 * action is a store-only update (the reducer runs synchronously, no network). This lets one caller
 * persist the change while another (e.g. a tab reacting to a cross-tab message) just mirrors the
 * resulting flag into its own store without re-doing the API work.
 * @param {Object} updatedValues The fields to merge into the user-settings slice.
 * @param {Object} [api] Optional api-middleware descriptor ({url, method, data}) to persist the change.
 * @returns {Object} An action object dispatched and (when api is present) treated by the api-middleware.
 */
export function setUserSettings(updatedValues, api) {
	return {
		type: types.UPDATE_USER_SETTINGS,
		updatedValues: updatedValues,
		...(api && {meta: {api}})
	};
}

/**
 * Persists a partial set of user settings to Universal so they sync as personal preferences across
 * every engagement. Universal's PUT validates and re-serializes the whole preference model, so the
 * authoritative settings fetched via getUserSettings are sent back, overriding only the fields in
 * newValues. Delegates to setUserSettings with the built api descriptor so the reducer can update
 * the slice once the PUT succeeds.
 * @param {Object} userSettings The Universal user-settings response from getUserSettings ({id, data}).
 * @param {Object} newValues The fields to overwrite (subset of the settings data, any key casing).
 * @returns {Object} An action object treated by the api-middleware (PUT to Universal users.json).
 */
export function updateUserSettings(userSettings, newValues) {
	const settings = userSettings.data;
	const updatedValues = normalizeKeysToReference(settings, newValues);
	const url = env.getUniversalUrl(`/users.json/${userSettings.id}`);

	const api = {
		url: url,
		method: 'PUT',
		data: {
			collections: {
				userscollection: [
					{
						data: {
							...settings,
							...updatedValues
						}
					}
				]
			}
		}
	};

	return setUserSettings(updatedValues, api);
}

/**
 * Returns an action to be dispatched and treated by the api-middleware, ultimately making an API call through Axios.
 * Represents a request for an Internal User in the specified Engagement.
 * @param {Number} engagementId The Engagement ID.
 * @param {String} userId The User ID.
 * @param {Boolean} expandIndependenceInformation A value that determines whether to retrieve User Independence information or not.
 * @returns {Object} An action object to be dispatched and treated by the api-middleware.
 */
export function getInternalUserById(engagementId, userId, expandIndependenceInformation) {
	let expand;

	if (expandIndependenceInformation) {
		expand = 'independence';
	}

	const url = GetByIdInternalUserUrl(engagementId, userId, {expand});

	return {
		type: types.GET_INTERNAL_USER_BY_ID,
		meta: {
			api: {
				url: url,
				method: 'GET'
			}
		}
	};
}
